import { useEffect, useContext, useCallback } from 'react';
import { WebSocketContext } from '../../context/WebSocketContext.js';
import { LearnosityContainerObservable } from './LearnosityContainer.js';
import {
  constructMessage,
  PWAEventsObservable,
} from './WebsocketMessageHandler.js';
import { LessThanSecureEventsObservable } from '../less-secure/LessSecure.js';
import { LEARNOSITY_EVENTS } from '../../scripts/learnosityScripts.js';
import { AssessmentContext } from '../../context/AssessmentContext.js';
import useSessionStorage from '../../hooks/useSessionStorage.js';
import * as Storage from '../common/Storage.js';
import * as TestStatus from './TestStatus.js';
import * as PwaEvents from '../../context/PwaEvents.js';
import {
  waitToResume,
  PROHIBITED_ACTION,
} from '../../scripts/less-secure/enforceLessSecure.js';
import { NETWORK_ERROR_ACTION } from '../../scripts/network-error/networkError.js';
import { PSYCHOMETRIC_EVENTS } from './PyschometricEvents.js';

const TEST_ENDED_ACTION = {
  EXIT: 'exit',
  SUBMIT: 'submit',
  TERMINATE: 'terminate',
};

const TEST_ENDED_PROCTOR_EVENT_DETAILS = {
  [LEARNOSITY_EVENTS.TEST_PROCTOR_SAVE]: { user: 'proctor', action: 'exit' },
  [LEARNOSITY_EVENTS.TEST_PROCTOR_EXIT_AND_SUBMIT]: {
    user: 'proctor',
    action: 'submit',
  },
  [LEARNOSITY_EVENTS.TEST_PROCTOR_TERMINATED]: {
    user: 'proctor',
    action: 'terminate',
  },
};

const PsychometricEventHandler = () => {
  const { getStorageItem } = useSessionStorage();
  const { sendMsgToWebsocket } = useContext(WebSocketContext);
  const {
    loginData,
    learnosityRequestObj,
    resetPausedResumeRelatedStorage,
    updateTestStatus,
  } = useContext(AssessmentContext);

  const TEST_RESUMED_EVENT_DETAILS = (actor) => {
    return {
      user: actor,
    };
  };

  const TEST_ENDED_EVENT_DETAILS = (actor, action, itemsAttempted) => {
    return {
      user: actor,
      action: action,
      ...(itemsAttempted && { itemsAttempted: itemsAttempted }),
    };
  };

  const getMessage = useCallback(
    (event, eventDetails) => {
      return constructMessage(
        event,
        eventDetails,
        learnosityRequestObj,
        loginData,
      );
    },
    [loginData, learnosityRequestObj],
  );

  const getAttemptedItems = (appInstance) => {
    const attemptedItemsId = appInstance.attemptedItems();
    let attemptedItems = [];
    if (!attemptedItemsId) {
      return attemptedItems;
    }
    for (let itemId of attemptedItemsId) {
      const itemPosition = appInstance.assessApp().getItemPosition(itemId);
      attemptedItems.push(itemPosition);
    }
    return attemptedItems;
  };

  const getTestStartDetails = useCallback((appInstance) => {
    const items = appInstance.getItems() || {};
    const numItems = Object.keys(items)?.length;
    const itemsAttempted = getAttemptedItems(appInstance);
    return { numItems, itemsAttempted };
  }, []);

  const getItemLoadedDetails = (appInstance) => {
    const currentItem = appInstance.getCurrentItem();
    const itemId = currentItem.reference;
    const itemSequence = appInstance.assessApp().getItemPosition(itemId);
    return { itemSequence, itemId };
  };

  const getToolUsedDetails = (appInstance, eventDetails) => {
    const currentItem = appInstance.getCurrentItem();
    const itemRefId = currentItem.reference;
    return { ...eventDetails, itemRefId };
  };

  const getItemMoveDetails = useCallback(async (appInstance) => {
    const currentItem = appInstance.getCurrentItem();
    const itemId = currentItem.reference;
    const itemSequence = appInstance.assessApp().getItemPosition(itemId);
    const itemsAttempted = getAttemptedItems(appInstance);

    const allResponses = appInstance.getResponses();

    const getScoresPromise = () => {
      return new Promise((resolve) => {
        const callbackFn = async (scores) => resolve(scores);
        appInstance.getScores(callbackFn);
      });
    };
    const allScores = await getScoresPromise();

    let questions = [];
    const currentItemQuestions = appInstance.getCurrentItem().questions;
    for (let index = 0; index < currentItemQuestions.length; index++) {
      const question = currentItemQuestions[index];
      const questionId = question.metadata.widget_reference;
      const questionResponseId = question.response_id;
      const questionResponse = allResponses[questionResponseId];
      const questionScore = allScores[questionResponseId];
      questions.push({
        questionSequence: index,
        questionId: questionId,
        response: {
          revision: questionResponse?.revision,
          type: questionResponse?.type,
          value: ['longtextV2'].includes(question.type)
            ? 'response was saved'
            : questionResponse?.value,
        },
        score:
          questionScore?.score === undefined || questionScore?.score === null
            ? 'none'
            : questionScore?.score,
      });
    }
    return { itemSequence, itemId, itemsAttempted, questions };
  }, []);

  const getPausedDetails = useCallback(
    (pausedReason) => {
      const __pausedBy = getStorageItem(Storage.STORAGE_PAUSED_BY) || TestStatus.PAUSED_BY_TESTER;
      const __testStatus = getStorageItem(Storage.STORAGE_TEST_STATUS);
      if (
        __testStatus === TestStatus.TEST_PAUSED &&
        __pausedBy === TestStatus.PAUSED_BY_PROCTOR
      ) {
        return { user: TestStatus.PAUSED_BY_PROCTOR };
      }
      if (
        __testStatus === TestStatus.TEST_PAUSED &&
        __pausedBy === TestStatus.PAUSED_BY_TESTER
      ) {
        return { user: TestStatus.PAUSED_BY_TESTER };
      }
      if (
        __testStatus === TestStatus.TEST_PAUSED &&
        __pausedBy === TestStatus.PAUSED_BY_SYSTEM
      ) {
        return {
          user: TestStatus.PAUSED_BY_SYSTEM,
          ...(pausedReason?.startsWith(PROHIBITED_ACTION.PREFIX) ||
          pausedReason?.startsWith(NETWORK_ERROR_ACTION.PREFIX)
            ? { action: pausedReason, waitToResume: waitToResume() }
            : {}),
        };
      }
    },
    [getStorageItem]
  );

  const sendTestEndedMessages = useCallback(
    async (appInstance, action) => {
      //for TEST_LAUNCHED, save and exit is handled in WebsocketMessageHandler, appInstance is null for that case
      sendMsgToWebsocket(
        getMessage(
          PSYCHOMETRIC_EVENTS.ITEM_MOVE,
          await getItemMoveDetails(appInstance),
        ),
        false,
      );

      let itemsAttempted = getAttemptedItems(appInstance);
      let actor = getStorageItem(Storage.STORAGE_PAUSED_BY);
      sendMsgToWebsocket(
        getMessage(
          PSYCHOMETRIC_EVENTS.TEST_ENDED,
          TEST_ENDED_EVENT_DETAILS(
            actor ? actor : PwaEvents.ACTOR_TESTER,
            action,
            itemsAttempted,
          ),
        ),
        false,
      );
    },
    [getItemMoveDetails, sendMsgToWebsocket, getMessage, getStorageItem],
  );

  const processResumeEvent = useCallback(() => {
    let pausedReason = getStorageItem(Storage.STORAGE_PAUSED_REASON);
    if (
      pausedReason === PwaEvents.ACTOR_PROCTOR_ENDED ||
      pausedReason === PwaEvents.ACTOR_TESTER_ENDED
    ) {
      // this help prevent sending resume events on proctor ended events - saveAndExit, submitAndExit, terminate
      return;
    }

    let resumeActor = getStorageItem(Storage.STORAGE_RESUMED_BY);
    sendMsgToWebsocket(
      getMessage(
        PSYCHOMETRIC_EVENTS.TEST_RESUMED,
        TEST_RESUMED_EVENT_DETAILS(
          resumeActor ? resumeActor : PwaEvents.ACTOR_TESTER,
        ),
      ),
      false,
    );
    let statusBeforePause =
      getStorageItem(Storage.STORAGE_STATUS_BEFORE_PAUSE) ||
      TestStatus.TEST_INSESSION;

    updateTestStatus(statusBeforePause);
    resetPausedResumeRelatedStorage();
  }, [
    getStorageItem,
    sendMsgToWebsocket,
    resetPausedResumeRelatedStorage,
    updateTestStatus,
    getMessage,
  ]);
  /**
   * Event handler to run before the events processing in
   * TestEventsHandler
   */
  const handleBeforeTestEvents = useCallback(
    async ({ appInstance, eventName, detail }) => {
      switch (eventName) {
        case LEARNOSITY_EVENTS.TEST_READY:
          let testStatus = getStorageItem(Storage.STORAGE_TEST_STATUS);
          if (testStatus !== TestStatus.TEST_PAUSED) {
            sendMsgToWebsocket(
              getMessage(PSYCHOMETRIC_EVENTS.TEST_READY),
              false
            );
          }
          break;
        case LEARNOSITY_EVENTS.TEST_START:
          sendMsgToWebsocket(
            getMessage(
              PSYCHOMETRIC_EVENTS.TEST_STARTED,
              getTestStartDetails(appInstance)
            ),
            false
          );
          break;
        case LEARNOSITY_EVENTS.ITEM_LOAD:
          sendMsgToWebsocket(
            getMessage(
              PSYCHOMETRIC_EVENTS.ITEM_LOADED,
              getItemLoadedDetails(appInstance)
            ),
            false
          );
          break;
        case LEARNOSITY_EVENTS.ITEM_BEFOREUNLOAD:
          sendMsgToWebsocket(
            getMessage(
              PSYCHOMETRIC_EVENTS.ITEM_MOVE,
              await getItemMoveDetails(appInstance)
            ),
            false
          );
          break;
        case PwaEvents.PROCTOR_SAVEANDEXIT:
        case PwaEvents.TESTER_SAVEANDEXIT: //This event is generated by NMC custom Save/Exit button
        case LEARNOSITY_EVENTS.TEST_FINISHED_SAVE: //This event is generated by Learnosity Save/Exit button
          await sendTestEndedMessages(appInstance, TEST_ENDED_ACTION.EXIT);
          break;
        //no need to listen to PwaEvents.PROCTOR_SUBMITANDEXIT, otherwise it will send 4 events(2 testEnded & 2 itemMove), TEST_SUBMIT_SUCCESS is enough for both proctor invoked & tester invoked.
        case LEARNOSITY_EVENTS.TEST_SUBMIT_SUCCESS:
          await sendTestEndedMessages(appInstance, TEST_ENDED_ACTION.SUBMIT);
          break;
        case PwaEvents.PROCTOR_TERMINATE:
        case LEARNOSITY_EVENTS.TEST_FINISHED_DISCARD:
          await sendTestEndedMessages(appInstance, TEST_ENDED_ACTION.TERMINATE);
          break;
        case LEARNOSITY_EVENTS.TEST_PROCTOR_SAVE: //TODO: in the future these 3 cases will go away, no need to listen to live progress/proctor events
        case LEARNOSITY_EVENTS.TEST_PROCTOR_EXIT_AND_SUBMIT:
        case LEARNOSITY_EVENTS.TEST_PROCTOR_TERMINATED:
          sendMsgToWebsocket(
            getMessage(
              PSYCHOMETRIC_EVENTS.TEST_ENDED,
              TEST_ENDED_PROCTOR_EVENT_DETAILS[eventName]
            ),
            false
          );
          break;
        case PwaEvents.LEARNOSITY_EMIT_TOOLUSED:
          sendMsgToWebsocket(
            getMessage(
              PSYCHOMETRIC_EVENTS.TOOL_USED,
              getToolUsedDetails(appInstance, detail)
            ),
            false
          );
          break;
        default:
          break;
      }
    },
    [
      sendMsgToWebsocket,
      getMessage,
      getItemMoveDetails,
      getTestStartDetails,
      sendTestEndedMessages,
      getStorageItem,
    ]
  );

  /**
   * Event handler to run after the events processing in
   * TestEventsHandler is complete
   */
  const handleAfterTestEvents = useCallback(
    async ({ appInstance, eventName }) => {
      switch (eventName) {
        case PwaEvents.PROCTOR_PAUSE: //this event is only triggered when getting a proctor paused but test is already in paused state
        case LEARNOSITY_EVENTS.TEST_PAUSE:
          //Note Proctor pause also triggers test:pause
          let pausedReason = getStorageItem(Storage.STORAGE_PAUSED_REASON);
          if (
            pausedReason === PwaEvents.ACTOR_PROCTOR_ENDED ||
            pausedReason === PwaEvents.ACTOR_TESTER_ENDED
          ) {
            // this help prevent sending pause events on proctor ended events - saveAndExit, submitAndExit, terminate
            break;
          }
          const pauseDetails = getPausedDetails(pausedReason); //TODO: in the future, this will change on how to determine pauseActor
          if (pauseDetails) {
            sendMsgToWebsocket(
              getMessage(PSYCHOMETRIC_EVENTS.TEST_PAUSED, pauseDetails),
              false,
            );
          }
          break;
        case PwaEvents.PROCTOR_RESUME: //this event is triggered when 1. testStatus is already in inSession 2. resuming on testLaunched state(isSecure) - because no Learnosity resume event, coming from WebsocketMessageHandler
        case LEARNOSITY_EVENTS.TEST_RESUME:
          processResumeEvent();
          break;
        default:
          break;
      }
    },
    [
      sendMsgToWebsocket,
      getMessage,
      getPausedDetails,
      getStorageItem,
      processResumeEvent,
    ],
  );

  const handleLessThanSecureEvents = useCallback(
    async ({ appInstance, eventName }) => {
      switch (eventName) {
        case PwaEvents.PROHIBITED_ACTION_PAUSE:
          let prohibitedAction = getStorageItem(
            Storage.STORAGE_PROHIBITED_ACTION,
          );
          let prohibitedActionDetails = {
            user: TestStatus.PAUSED_BY_SYSTEM,
            action: prohibitedAction,
            waitToResume: waitToResume(),
          };
          sendMsgToWebsocket(
            getMessage(
              PSYCHOMETRIC_EVENTS.TEST_PAUSED,
              prohibitedActionDetails,
            ),
            false,
          );
          break;
        case PwaEvents.READY_TO_RESUME:
          sendMsgToWebsocket(
            getMessage(PSYCHOMETRIC_EVENTS.READY_TO_RESUME, {
              user: PwaEvents.ACTOR_SYSTEM,
            }),
            false,
          );
          break;
        case PwaEvents.PROCTOR_RESUME: //this event is triggered from lessthan secure when proctor resumes on testLaunched status
        case PwaEvents.TESTER_RESUME:
          processResumeEvent();
          break;
        default:
          break;
      }
    },
    [getMessage, getStorageItem, sendMsgToWebsocket, processResumeEvent],
  );

  //UseEffect required. Subscribe and unsubscribe here
  //to make sure the event handler is registered and unregistered
  useEffect(() => {
    //priority on subscribe will call the methods in priority order
    //otherwise it will get invoked in the order of subscription

    //Before TestEvents so priorty is 1.
    LearnosityContainerObservable.subscribe(
      handleBeforeTestEvents,
      'psychometricEventsPreTestEventsHandler',
      1
    );

    //After TestEvents so priorty is 3.
    LearnosityContainerObservable.subscribe(
      handleAfterTestEvents,
      'psychometricEventPostTestEventsHandler',
      3
    );

    PWAEventsObservable.subscribe(
      handleBeforeTestEvents,
      'psychometricEventsPreTestEventsHandler',
      1
    );

    PWAEventsObservable.subscribe(
      handleAfterTestEvents,
      'psychometricEventsPostTestEventsHandler',
      3,
    );

    LessThanSecureEventsObservable.subscribe(
      handleLessThanSecureEvents,
      'lessThanSecureEventsHandler',
      1,
    );

    return () => {
      LearnosityContainerObservable.unsubscribe(
        'psychometricEventsPreTestEventsHandler',
      );
      LearnosityContainerObservable.unsubscribe(
        'psychometricEventPostTestEventsHandler',
      );
      PWAEventsObservable.unsubscribe('psychometricEventsPreTestEventsHandler');
      LessThanSecureEventsObservable.unsubscribe('lessThanSecureEventsHandler');
    };
  }, [
    handleBeforeTestEvents,
    handleAfterTestEvents,
    handleLessThanSecureEvents,
  ]);

  return <></>;
};
export default PsychometricEventHandler;
